/**
 * InsightDonutChart Component
 *
 * Displays insights by type as a donut/pie chart.
 */

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Tag } from 'lucide-react';

// Distinct colors for each insight type
const TYPE_COLORS = {
  'architecture': '#8B5CF6',       // Purple
  'blocker': '#EF4444',            // Red
  'bug': '#F97316',                // Orange
  'bug-fix': '#10B981',            // Green
  'deployment': '#3B82F6',         // Blue
  'feature-complete': '#06B6D4',   // Cyan
  'improvement': '#84CC16',        // Lime
  'pattern': '#EC4899',            // Pink
  'preference': '#F59E0B',         // Amber
  'process': '#6366F1',            // Indigo
  'solution': '#14B8A6',           // Teal
  'test': '#A855F7',               // Violet
  'decision': '#E879F9',           // Fuchsia
  'learning': '#22D3EE',           // Light Cyan
  'issue': '#FB7185',              // Rose
  'optimization': '#34D399',       // Emerald
};

// Fallback palette for unknown types - deterministic via hash
const FALLBACK_COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];

const getColor = (type) => {
  if (TYPE_COLORS[type]) return TYPE_COLORS[type];
  // Simple hash to pick a consistent color for unknown types
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = ((hash << 5) - hash + type.charCodeAt(i)) | 0;
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-sm font-medium text-white">{payload[0].name}</p>
      <p className="text-xs text-gray-400">{payload[0].value} Insights</p>
    </div>
  );
};

const renderLegend = (props) => {
  const { payload } = props;
  return (
    <ul className="flex flex-wrap justify-center gap-3 mt-2">
      {payload.map((entry, index) => (
        <li key={`legend-${index}`} className="flex items-center gap-1.5 text-xs text-gray-400">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </li>
      ))}
    </ul>
  );
};

export default function InsightDonutChart({ data = [], isLoading = false }) {
  if (isLoading) {
    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="w-5 h-5 text-green-400" />
          <h3 className="text-sm font-medium text-white">Insights by Type</h3>
        </div>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-pulse text-gray-500">Lade Daten...</div>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="w-5 h-5 text-green-400" />
          <h3 className="text-sm font-medium text-white">Insights by Type</h3>
        </div>
        <div className="h-64 flex items-center justify-center text-gray-500">
          Keine Daten vorhanden
        </div>
      </div>
    );
  }

  // Transform data for PieChart
  const chartData = data.map((item) => ({
    name: item.type,
    value: item.count,
  }));

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Tag className="w-5 h-5 text-green-400" />
        <h3 className="text-sm font-medium text-white">Insights by Type</h3>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="40%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getColor(entry.name)} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={renderLegend} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
